/**
 * Service Domain Configuration - Environment-Aware URLs
 *
 * Domains are defined in /config/domains.json (the canonical source).
 * This file reads from the JSON and provides TypeScript types and helpers.
 *
 * For local dev: Uses ports.json defaults (localhost URLs)
 * For docker/staging/production: Uses domains.json for environment-specific URLs
 *
 * @example
 * ```typescript
 * import { getServiceUrl, getDomains } from '@ops-matrix/shared-types';
 *
 * // Get URL for current environment
 * const apiUrl = getServiceUrl('API_GATEWAY'); // Auto-detects environment
 *
 * // Get all domains for a specific environment
 * const stagingDomains = getDomains('staging');
 * console.log(stagingDomains.portals.business); // portal.opsmatrix.dev
 * ```
 *
 * @module config/domains
 */

import domainsJson from '../../config/domains.json';

/**
 * Environment types
 */
export type Environment = 'local' | 'docker' | 'staging' | 'production';

/**
 * Service categories
 */
export type ServiceCategory = 'backend' | 'portals' | 'monitoring' | 'internal';

/**
 * Internal Cloud Run service group keys
 */
export type InternalServiceGroup = 'BACKEND_API' | 'BACKEND_CORE' | 'BACKEND_AI' | 'BACKEND_DOCS';

/**
 * Service keys
 */
export type BackendService = keyof typeof domainsJson.environments.local.backend;
export type PortalService = keyof typeof domainsJson.environments.local.portals;
export type MonitoringService = keyof typeof domainsJson.environments.staging.monitoring;

export type ServiceKey = BackendService | PortalService | MonitoringService;

/**
 * Domain configuration for an environment
 */
export interface EnvironmentDomains {
  protocol: string;
  host: string;
  /** Load balancer IP for DNS A-record validation (custom domains) */
  loadBalancerIp: string;
  /** Convenience accessor for API Gateway domain */
  apiGateway: string;
  backend: Record<string, string>;
  portals: {
    admin: string;
    business: string;
    consumer: string;
    auth: string;
    /** Raw portal map keyed by service name (e.g., ADMIN_PORTAL) */
    [key: string]: string;
  };
  monitoring?: Record<string, string>;
}

/**
 * Build an EnvironmentDomains object from the raw JSON entry,
 * adding convenience accessors (apiGateway, portals.admin, etc.)
 */
function buildEnvironmentDomains(
  raw: (typeof domainsJson.environments)[keyof typeof domainsJson.environments],
): EnvironmentDomains {
  const backend = (raw as { backend?: Record<string, string> }).backend ?? {};
  const rawPortals = (raw as { portals?: Record<string, string> }).portals ?? {};
  const monitoring = (raw as { monitoring?: Record<string, string> }).monitoring;

  // Build portals with convenience keys
  const portals: EnvironmentDomains['portals'] = {
    admin: rawPortals.ADMIN_PORTAL ?? '',
    business: rawPortals.BUSINESS_PORTAL ?? '',
    consumer: rawPortals.CONSUMER_PORTAL ?? '',
    auth: rawPortals.AUTH_PORTAL ?? '',
    // Also include the raw keys for backward compat
    ...rawPortals,
  };

  return {
    protocol: (raw as { protocol: string }).protocol,
    host: (raw as { host: string }).host,
    loadBalancerIp: (raw as { loadBalancerIp: string }).loadBalancerIp,
    apiGateway: backend.API_GATEWAY ?? '',
    backend,
    portals,
    ...(monitoring ? { monitoring } : {}),
  };
}

/**
 * Get all domains for a specific environment
 *
 * @param environment - Environment name
 * @returns Domain configuration for the environment with convenience accessors
 *
 * @example
 * ```typescript
 * const stagingDomains = getDomains('staging');
 * console.log(stagingDomains.apiGateway); // api.opsmatrix.dev
 * console.log(stagingDomains.portals.business); // portal.opsmatrix.dev
 * ```
 */
export function getDomains(environment: Environment): EnvironmentDomains {
  return buildEnvironmentDomains(domainsJson.environments[environment]);
}

/**
 * Detect current environment
 * Uses NODE_ENV and common environment variables
 *
 * @returns Detected environment
 */
export function detectEnvironment(): Environment {
  // Server-side (Node.js)
  if (typeof process !== 'undefined' && process.env) {
    // Check explicit environment variable
    const envVar = process.env.DEPLOY_ENV || process.env.ENVIRONMENT;
    if (envVar === 'staging') return 'staging';
    if (envVar === 'production') return 'production';
    if (envVar === 'docker') return 'docker';

    // Check NODE_ENV
    if (process.env.NODE_ENV === 'production') {
      // In production, default to production unless explicitly set to staging
      return 'production';
    }
    if (process.env.NODE_ENV === 'development') {
      return 'local';
    }
  }

  // Client-side (browser) - check location
  // biome-ignore lint/suspicious/noExplicitAny: Need to check for window in browser environment
  const globalAny = globalThis as any;
  if (typeof globalAny.window !== 'undefined' && globalAny.location) {
    const hostname: string | undefined = globalAny.location.hostname;
    if (typeof hostname === 'string' && hostname.length > 0) {
      const isStagingDomain =
        hostname === 'opsmatrix.dev' ||
        hostname.endsWith('.opsmatrix.dev') ||
        hostname === 'fiops.dev' ||
        hostname.endsWith('.fiops.dev');
      const isProductionDomain =
        hostname === 'opsmatrix.ai' ||
        hostname.endsWith('.opsmatrix.ai') ||
        hostname === 'fiops.ai' ||
        hostname.endsWith('.fiops.ai');
      if (isStagingDomain) return 'staging';
      if (isProductionDomain) return 'production';
      if (hostname === 'localhost' || hostname === '127.0.0.1') return 'local';
    }
  }

  // Default to local
  return 'local';
}

/**
 * Get service URL for current environment
 *
 * @param service - Service key
 * @param options - Options
 * @returns Service URL with protocol
 *
 * @example
 * ```typescript
 * // Auto-detects environment
 * const apiUrl = getServiceUrl('API_GATEWAY');
 * // Local: http://localhost:43000
 * // Staging: https://api.opsmatrix.dev
 * // Production: https://api.fiops.ai
 *
 * // Force specific environment
 * const stagingUrl = getServiceUrl('API_GATEWAY', { environment: 'staging' });
 * ```
 */
export function getServiceUrl(
  service: ServiceKey,
  options?: {
    environment?: Environment;
    withProtocol?: boolean;
  },
): string {
  const env = options?.environment ?? detectEnvironment();
  const withProtocol = options?.withProtocol ?? true;
  const domains = getDomains(env);

  // Find service in backend, portals, or monitoring
  let domain: string | undefined;
  if (service in domains.backend) {
    domain = domains.backend[service as string];
  } else if (service in domains.portals) {
    domain = domains.portals[service as string];
  } else if (domains.monitoring && service in domains.monitoring) {
    domain = domains.monitoring[service as string];
  }

  if (!domain) {
    throw new Error(`Service ${service} not found in domains.json for environment ${env}`);
  }

  // Return with or without protocol
  if (withProtocol) {
    // Check if domain already has protocol
    if (domain.startsWith('http://') || domain.startsWith('https://')) {
      return domain;
    }
    return `${domains.protocol}://${domain}`;
  }

  // Strip protocol if present
  return domain.replace(/^https?:\/\//, '');
}

/**
 * Get all service URLs for current environment
 *
 * @param envOrOptions - Environment string or options object
 * @returns Map of service keys to URLs
 *
 * @example
 * ```typescript
 * const urls = getAllServiceUrls('staging');
 * console.log(urls.API_GATEWAY); // https://api.opsmatrix.dev
 * console.log(urls.BUSINESS_PORTAL); // https://portal.opsmatrix.dev
 * ```
 */
export function getAllServiceUrls(envOrOptions?: Environment | { environment?: Environment }): Record<string, string> {
  const env = typeof envOrOptions === 'string' ? envOrOptions : (envOrOptions?.environment ?? detectEnvironment());
  const domains = getDomains(env);
  const urls: Record<string, string> = {};

  // Add backend services
  for (const [key, domain] of Object.entries(domains.backend)) {
    urls[key] = domain.startsWith('http') ? domain : `${domains.protocol}://${domain}`;
  }

  // Add portal services - use the raw keys (ADMIN_PORTAL, BUSINESS_PORTAL, etc.)
  for (const [key, domain] of Object.entries(domains.portals)) {
    // Skip convenience keys (admin, business, consumer, auth)
    if (['admin', 'business', 'consumer', 'auth'].includes(key)) continue;
    urls[key] = domain.startsWith('http') ? domain : `${domains.protocol}://${domain}`;
  }

  // Add monitoring services (if present)
  if (domains.monitoring) {
    for (const [key, domain] of Object.entries(domains.monitoring)) {
      urls[key] = domain.startsWith('http') ? domain : `${domains.protocol}://${domain}`;
    }
  }

  return urls;
}

/**
 * Check if running in a specific environment
 *
 * @param environment - Environment to check
 * @returns True if running in the specified environment
 *
 * @example
 * ```typescript
 * if (isEnvironment('production')) {
 *   // Production-only code
 * }
 * ```
 */
export function isEnvironment(environment: Environment): boolean {
  return detectEnvironment() === environment;
}

/**
 * Get internal Cloud Run URL for a service group.
 * These are stable across deployments and used for cross-group gRPC communication.
 *
 * @param group - Internal service group key (e.g., 'BACKEND_CORE')
 * @param options - Options (environment, format)
 * @returns Internal Cloud Run URL in requested format
 *
 * @example
 * ```typescript
 * // Default: host:443 format (for gRPC)
 * getInternalUrl('BACKEND_CORE'); // "backend-core-aqr6q7fgba-uc.a.run.app:443"
 *
 * // Just the hostname (no port)
 * getInternalUrl('BACKEND_CORE', { format: 'host' }); // "backend-core-aqr6q7fgba-uc.a.run.app"
 *
 * // Full HTTPS URL
 * getInternalUrl('BACKEND_CORE', { format: 'https' }); // "https://backend-core-aqr6q7fgba-uc.a.run.app"
 * ```
 */
export function getInternalUrl(
  group: InternalServiceGroup,
  options?: {
    environment?: Environment;
    format?: 'grpc' | 'host' | 'https';
  },
): string {
  const env = options?.environment ?? detectEnvironment();
  const format = options?.format ?? 'grpc';

  // biome-ignore lint/suspicious/noExplicitAny: domains.json internal section is not in the EnvironmentDomains type
  const envConfig = domainsJson.environments[env] as any;
  const internal = envConfig?.internal as Record<string, string> | undefined;

  if (!internal || !internal[group]) {
    throw new Error(
      `Internal URL for ${group} not found in domains.json for environment ${env}. ` +
        'Populate the internal section after first deployment.',
    );
  }

  const host = internal[group];

  // Reject placeholder URLs — production domains.json ships with "xxxx" placeholders
  // that must be replaced after first deployment. Fail fast with a clear message
  // instead of letting obscure DNS errors surface at runtime.
  if (host.includes('xxxx')) {
    throw new Error(
      `Internal URL for ${group} in ${env} is not populated (contains placeholder 'xxxx'). ` +
        'Populate domains.json with actual Cloud Run URLs after first deployment.',
    );
  }

  switch (format) {
    case 'https':
      return `https://${host}`;
    case 'host':
      return host;
    case 'grpc':
    default:
      return `${host}:443`;
  }
}

/**
 * Get all internal Cloud Run URLs for an environment.
 * Useful for syncing to Secret Manager or validating deployment.
 *
 * @param options - Options (environment, format)
 * @returns Map of group keys to URLs
 *
 * @example
 * ```typescript
 * const urls = getAllInternalUrls({ environment: 'staging', format: 'grpc' });
 * // { BACKEND_CORE: "backend-core-xxx.a.run.app:443", ... }
 * ```
 */
export function getAllInternalUrls(options?: {
  environment?: Environment;
  format?: 'grpc' | 'host' | 'https';
}): Record<InternalServiceGroup, string> {
  const env = options?.environment ?? detectEnvironment();

  // biome-ignore lint/suspicious/noExplicitAny: domains.json internal section is not in the EnvironmentDomains type
  const envConfig = domainsJson.environments[env] as any;
  const internal = envConfig?.internal as Record<string, string> | undefined;

  if (!internal) {
    throw new Error(`No internal URLs found in domains.json for environment ${env}`);
  }

  const result: Record<string, string> = {};
  for (const [key, host] of Object.entries(internal)) {
    if (key.startsWith('_')) continue; // Skip _comment
    const format = options?.format ?? 'grpc';
    switch (format) {
      case 'grpc':
        result[key] = `${host}:443`;
        break;
      case 'https':
        result[key] = `https://${host}`;
        break;
      case 'host':
        result[key] = host;
        break;
    }
  }

  return result as Record<InternalServiceGroup, string>;
}

/**
 * Export raw domains.json for advanced use cases
 */
export const DOMAINS_JSON = domainsJson;
